import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import InspectionContext from '../../../contexts/InspectionContext';
import PrintModal from '../../Shared/Print/PrintModal';

export interface ISiteMapPrintProps {
  onClose: () => void;
  targetDoc?: any;
  projectLevelEsriEnabled?: boolean;
  isGISEnabled?: boolean;
}

// True when the upload (or scope) carries an ESRI overlay worth offering in print
const isEsriEligible = (
  targetDoc?: any,
  projectLevelEsriEnabled?: boolean,
  isGISEnabled?: boolean,
) => {
  if (!targetDoc) return projectLevelEsriEnabled && isGISEnabled; // project-level scope — show; backend gates per-upload
  const pu = targetDoc.ProjectUploadModel || targetDoc;
  const t = pu?.site_map_type;
  return (
    (t === 'gis_esri' || t === 'gis_image_esri') &&
    !!pu?.gis_calibrated &&
    projectLevelEsriEnabled &&
    isGISEnabled
  );
};

/**
 * SW2-2345 — the scope choices that appear under "Include Changelog".
 *
 * Shown only while the checkbox above them is ticked: an always-visible sub-choice for
 * an option that is off reads as a second, independent setting.
 */
export const CHANGELOG_SCOPE_OPTIONS = [
  { label: 'All Logs', value: 'all' },
  { label: "This Version's Log Only", value: 'version' },
];

export const LEGEND_ITEM_TAG_SCOPE_OPTIONS = [
  { label: 'All Legend Items', value: 'all' },
  { label: "This Version's Legend Items (Edits, Additions)", value: 'version' },
];

const scopeSubOption = (options: { label: string; value: string }[]) => ({
  controlType: UIControlType.radio,
  options,
  // Indented under its checkbox so the pair reads as one setting, matching how the
  // version list below is inset from the "Site Maps" heading.
  style: { paddingLeft: '1.75rem', marginTop: '-0.5rem' },
  className: 'form-group--checkboxGroup',
});

export const getMapOptionsSchema = (
  targetDocId: any,
  targetDoc?: any,
  projectLevelEsriEnabled?: boolean,
  isGISEnabled?: boolean,
  formData?: {
    include_map_changelog?: boolean;
    include_legend_item_tags?: boolean;
  } & Record<string, any>
) => ({
  map_options: {
    label: 'Map Options',
    controlType: UIControlType.plainText,
    style: { marginBottom: 0 },
  },
  include_map_changelog: {
    label: 'Include Changelog',
    controlType: UIControlType.checkbox,
    style: {},
  },
  ...(formData?.include_map_changelog && {
    change_log_scope: scopeSubOption(CHANGELOG_SCOPE_OPTIONS),
  }),
  include_legend_item_tags: {
    label: 'Include Legend Item Tags',
    controlType: UIControlType.checkbox,
    style: {},
  },
  // SW2-2345. Shown only while the checkbox is ticked, for the same reason as the
  // changelog pair above: an always-visible sub-choice under an option that is off
  // reads as a second, independent setting.
  ...(formData?.include_legend_item_tags && {
    legend_item_tag_scope: scopeSubOption(LEGEND_ITEM_TAG_SCOPE_OPTIONS),
  }),
  include_blank_maps: {
    label: targetDocId === 0 ? 'Include Blank Maps' : 'Include Blank Map',
    controlType: UIControlType.checkbox,
    style: {},
  },
  ...(isEsriEligible(targetDoc, projectLevelEsriEnabled, isGISEnabled) && {
    include_esri_layers: {
      label: 'Include ESRI Layers',
      controlType: UIControlType.checkbox,
      style: {},
    },
  }),
});

/**
 * @param {{[k:string]: any}} formData
 */
const getPrintSiteMapSchema = (
  targetDocId: any,
  targetDoc?: any,
  projectLevelEsriEnabled?: boolean,
  isGISEnabled?: boolean,
  formData?: { include_map_changelog?: boolean } & Record<string, any>
) => {
  const schema: {
    [key: string]: any;
  } = {
    ...getMapOptionsSchema(
      targetDocId,
      targetDoc,
      projectLevelEsriEnabled,
      isGISEnabled,
      formData
    ),
  };

  return schema;
};

const SiteMapPrint: React.FC<ISiteMapPrintProps> = ({
  onClose,
  targetDoc,
  projectLevelEsriEnabled,
  isGISEnabled,
}) => {
  const context = useContext(InspectionContext);

  return (
    <PrintModal
      jobName='sitemap'
      jobData={{
        inspectionId: context.inspectionId,
        targetDocId: targetDoc ? targetDoc.id : null,
      }}
      statusUpdateKey={context.inspectionId}
      onClose={onClose}
      // A function, not an object: the changelog scope radio appears and disappears
      // with its checkbox, so the schema has to be rebuilt as the form changes.
      formSchema={(formData: { include_map_changelog?: boolean }) =>
        getPrintSiteMapSchema(
          targetDoc ? targetDoc.id : 0,
          targetDoc,
          projectLevelEsriEnabled,
          isGISEnabled,
          formData
        )
      }
      initialFormData={{
        include_map_changelog: false,
        include_esri_layers: false,
        // SW2-2345. Set up front so the radio has a selection the moment it appears,
        // and so an unchanged form still posts the documented default.
        change_log_scope: 'all',
        legend_item_tag_scope: 'all',
      }}
      modalTitle='Print Site Map'
      validateData={null}
    />
  );
};

SiteMapPrint.propTypes = {
  onClose: PropTypes.func.isRequired,
  targetDoc: PropTypes.object,
};

export default SiteMapPrint;
